import { FC } from "react";
import Table from "react-bootstrap/Table";
import Alert from "react-bootstrap/Alert";
import { FaEdit, FaEye } from "react-icons/fa";
import { ReportVersionTestDetailsQuery, useReportVersionTestDetailsQuery } from "app/api/graph/types";
import { reportVersionGraphId } from "app/api/graph/helpers";
import {
    editReportVersionAccessInformationRoute,
    editReportVersionEngagementOverviewRoute,
    editReportVersionScopeRoute,
    reportVersionAccessInformationRoute,
    reportVersionDeliverableFilesRoute,
    reportVersionEngagementOverviewRoute,
    reportVersionScopeRoute,
    reportVersionTestTypePrerequisitesRoute,
} from "app/route/Routes";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";
import { ButtonLink } from "app/component/button/ButtonLink";
import { useGeneralPermissions } from "app/api/graph/hook/use-general-permissions";

type RowProps = {
    reportId: number;
    version: NonNullable<ReportVersionTestDetailsQuery["reportVersion"]>;
};

const EngagementOverviewRow: FC<RowProps> = ({ reportId, version }) => {
    const engagementOverview = version?.engagementOverview;
    const { canView, canUpdate } = useGeneralPermissions(engagementOverview?.permissions);

    if (engagementOverview == null) {
        return null;
    }

    return (
        <tr>
            <td>Engagement overview</td>
            <td>
                {canView && (
                    <ButtonLink
                        to={reportVersionEngagementOverviewRoute.build({
                            reportId,
                            versionId: version._id,
                        })}
                        variant="info"
                        size="sm"
                        title="View"
                    >
                        <FaEye />
                    </ButtonLink>
                )}
                {canUpdate && (
                    <ButtonLink
                        to={editReportVersionEngagementOverviewRoute.build({
                            reportId,
                            versionId: version._id,
                        })}
                        variant="warning"
                        size="sm"
                        title="Edit"
                    >
                        <FaEdit />
                    </ButtonLink>
                )}
            </td>
        </tr>
    );
};

const ScopeRow: FC<RowProps> = ({ reportId, version }) => {
    const scope = version?.scope;
    const { canView, canUpdate } = useGeneralPermissions(scope?.permissions);

    if (scope == null) {
        return null;
    }

    return (
        <tr>
            <td>Scope</td>
            <td>
                {canView && (
                    <ButtonLink
                        to={reportVersionScopeRoute.build({
                            reportId,
                            versionId: version._id,
                        })}
                        variant="info"
                        size="sm"
                        title="View"
                    >
                        <FaEye />
                    </ButtonLink>
                )}
                {canUpdate && (
                    <ButtonLink
                        to={editReportVersionScopeRoute.build({
                            reportId,
                            versionId: version._id,
                        })}
                        variant="warning"
                        size="sm"
                        title="Edit"
                    >
                        <FaEdit />
                    </ButtonLink>
                )}
            </td>
        </tr>
    );
};

const AccessInformationRow: FC<RowProps> = ({ reportId, version }) => {
    const accessInformation = version?.accessInformation;
    const { canView, canUpdate } = useGeneralPermissions(accessInformation?.permissions);

    if (accessInformation == null) {
        return null;
    }

    return (
        <tr>
            <td>Access information</td>
            <td>
                {canView && (
                    <ButtonLink
                        to={reportVersionAccessInformationRoute.build({
                            reportId,
                            versionId: version._id,
                        })}
                        variant="info"
                        size="sm"
                        title="View"
                    >
                        <FaEye />
                    </ButtonLink>
                )}
                {canUpdate && (
                    <ButtonLink
                        to={editReportVersionAccessInformationRoute.build({
                            reportId,
                            versionId: version._id,
                        })}
                        variant="warning"
                        size="sm"
                        title="Edit"
                    >
                        <FaEdit />
                    </ButtonLink>
                )}
            </td>
        </tr>
    );
};

const TestTypePrerequisitesRow: FC<RowProps> = ({ reportId, version }) => (
    <tr>
        <td>Prerequisites</td>
        <td>
            <ButtonLink
                to={reportVersionTestTypePrerequisitesRoute.build({
                    reportId,
                    versionId: version._id,
                })}
                variant="info"
                size="sm"
                title="View"
            >
                <FaEye />
            </ButtonLink>
        </td>
    </tr>
);

const DeliverableFilesRow: FC<RowProps> = ({ reportId, version }) => (
    <tr>
        <td>Deliverable files</td>
        <td>
            <ButtonLink
                to={reportVersionDeliverableFilesRoute.build({
                    reportId,
                    versionId: version._id,
                })}
                variant="info"
                size="sm"
                title="View"
            >
                <FaEye />
            </ButtonLink>
        </td>
    </tr>
);

type Props = {
    reportId: number;
    versionId: number;
};

export const ReportVersionTestDetailsTable: FC<Props> = ({ reportId, versionId }) => {
    const { data, loading, error } = useReportVersionTestDetailsQuery({
        variables: { versionId: reportVersionGraphId(versionId) },
        notifyOnNetworkStatusChange: true,
    });
    const version = data?.reportVersion;

    if (loading && !version) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!version) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    return (
        <Table responsive="sm" bordered className="crud-table">
            <thead>
                <tr>
                    <th>Section</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                <EngagementOverviewRow reportId={reportId} version={version} />
                <ScopeRow reportId={reportId} version={version} />
                <AccessInformationRow reportId={reportId} version={version} />
                <TestTypePrerequisitesRow reportId={reportId} version={version} />
                <DeliverableFilesRow reportId={reportId} version={version} />
            </tbody>
        </Table>
    );
};
